export type MailAttachmentPayload = {
  filename: string;
  content_type: string;
  content_base64: string;
};

export type LocalMailAttachment = {
  id: string;
  file: File;
};

export function createLocalMailAttachment(file: File): LocalMailAttachment {
  return {
    id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
    file,
  };
}

export function localMailAttachmentsFromFileList(fileList: FileList | File[]): LocalMailAttachment[] {
  return Array.from(fileList).map(createLocalMailAttachment);
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Datei konnte nicht gelesen werden."));
    reader.readAsDataURL(file);
  });
}

export async function encodeLocalMailAttachments(
  entries: LocalMailAttachment[],
): Promise<MailAttachmentPayload[]> {
  const out: MailAttachmentPayload[] = [];
  for (const entry of entries) {
    const content_base64 = await fileToBase64(entry.file);
    out.push({
      filename: entry.file.name,
      content_type: entry.file.type || "application/octet-stream",
      content_base64,
    });
  }
  return out;
}

/** Letzte Dateiendung ohne Punkt, kleingeschrieben; leer wenn keine Endung. */
export function fileExtensionKey(name: string | null | undefined): string {
  const base = String(name || "")
    .trim()
    .split(/[\\/]/)
    .pop() || "";
  const dot = base.lastIndexOf(".");
  if (dot <= 0 || dot >= base.length - 1) return "";
  return base.slice(dot + 1).toLowerCase();
}

export function formatFileExtensionLabel(ext: string): string {
  return ext ? `.${ext}` : "ohne Endung";
}

/** Vorhandene Endungen, häufigste zuerst. */
export function uniqueFileExtensions(names: Array<string | null | undefined>): string[] {
  const counts = new Map<string, number>();
  for (const name of names) {
    const ext = fileExtensionKey(name);
    counts.set(ext, (counts.get(ext) || 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => {
      if (!a[0] && b[0]) return 1;
      if (a[0] && !b[0]) return -1;
      return b[1] - a[1] || a[0].localeCompare(b[0], "de");
    })
    .map(([ext]) => ext);
}

export function formatAttachmentSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function normalizeProofPdfKey(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/-signed(?=\.pdf$)/, "")
    .replace(/_/g, "-")
    .replace(/\.pdf$/, "");
}

export function isSignedProofPdfFilename(name: string): boolean {
  const n = name.trim().toLowerCase();
  return n.endsWith("-signed.pdf") && !n.endsWith("-anlagen.pdf");
}

export function isProofDocumentPdfFilename(name: string): boolean {
  const n = name.trim().toLowerCase();
  if (!n.endsWith(".pdf") || n.endsWith("-anlagen.pdf")) return false;
  return n.includes("stundennachweis") || n.includes("lieferschein");
}

/** Klartext, welche Lieferschein-PDF der Mail-Anhang ist. */
export function lieferscheinMailVariantLabel(hasSignedPdf: boolean): string {
  return hasSignedPdf
    ? "Unterschriebenes PDF mit Kundenunterschrift"
    : "Erstelltes PDF ohne Kundenunterschrift";
}

/** Zusatzzeile im Projektordner: signed-Dateiname allein ist nicht eindeutig. */
export function sharePointProofVariantLabel(name: string): string | null {
  if (!isProofDocumentPdfFilename(name)) return null;
  const n = name.trim().toLowerCase();
  const kind = n.includes("lieferschein") ? "Lieferschein" : "Stundennachweis";
  return isSignedProofPdfFilename(name)
    ? `${kind} mit Kundenunterschrift`
    : `${kind} ohne Kundenunterschrift`;
}

/** Unsignierte Stundennachweis-/Lieferschein-PDFs ausblenden, wenn eine *-signed.pdf existiert. */
export function preferSignedProofSharepointItems<T extends { name?: string | null }>(items: T[]): T[] {
  const signedKeys = new Set<string>();
  for (const it of items) {
    const name = String(it.name || "");
    if (isSignedProofPdfFilename(name) && isProofDocumentPdfFilename(name)) {
      signedKeys.add(normalizeProofPdfKey(name));
    }
  }
  if (!signedKeys.size) return items;
  return items.filter((it) => {
    const name = String(it.name || "");
    if (!isProofDocumentPdfFilename(name) || isSignedProofPdfFilename(name)) return true;
    return !signedKeys.has(normalizeProofPdfKey(name));
  });
}
